import { useState, useEffect } from 'react';
import { useAuth } from '../../../auth/firebaseAuth';
import {
  deleteDocument,
  insertDocument,
  queryDocuments
} from '../../../utility/firebaseHelpers';
import { serverTimestamp, where } from 'firebase/firestore';
import { useAnalyticsWithConsent } from '../../../hooks/useAnalyticsWithConsent';
import { AnalyticsEvents } from '../../../analytics/firebaseAnalytics';

interface UseBookmarksResult {
  bookmarkedListings: number[];
  handleBookmarkToggle: (
    id: number,
    currentPage: number,
    listings: any[]
  ) => Promise<void>;
}

export const useBookmarks = (): UseBookmarksResult => {
  const { currentUser, loading } = useAuth();
  const [bookmarkedListings, setBookmarkedListings] = useState<number[]>([]);
  const analytics = useAnalyticsWithConsent();

  useEffect(() => {
    const fetchBookmarks = async () => {
      if (currentUser) {
        try {
          const bookmarks = await queryDocuments('bookmarks', [
            where('userId', '==', currentUser.uid)
          ]);
          const bookmarkedIds = bookmarks.map((bookmark) =>
            Number(bookmark.listingId)
          );
          setBookmarkedListings(bookmarkedIds);
        } catch (error) {
          console.error('Error fetching bookmarks:', error);
        }
      } else {
        const storedBookmarks = localStorage.getItem('bookmarkedListings');
        if (storedBookmarks) {
          setBookmarkedListings(JSON.parse(storedBookmarks));
        }
      }
    };

    if (!loading) {
      fetchBookmarks();
    }
  }, [currentUser, loading]);

  const handleBookmarkToggle = async (
    id: number,
    currentPage: number,
    listings: any[]
  ) => {
    analytics.logEvent(AnalyticsEvents.BOOKMARK_INTERACTION, {
      property_id: id,
      action: bookmarkedListings.includes(id) ? 'remove' : 'add',
      source: AnalyticsEvents.SEARCH_RESULTS,
      page_number: currentPage,
      position_in_results: listings.findIndex((l) => l.id === id) + 1,
      authenticated: !!currentUser
    });

    if (currentUser) {
      try {
        const bookmarks = await queryDocuments('bookmarks', [
          where('userId', '==', currentUser.uid),
          where('listingId', '==', id.toString())
        ]);

        if (bookmarks.length === 0) {
          await insertDocument('bookmarks', {
            userId: currentUser.uid,
            listingId: id.toString(),
            createdAt: serverTimestamp()
          });
          setBookmarkedListings((prev) => [...prev, id]);
        } else {
          await deleteDocument('bookmarks', bookmarks[0].id);
          setBookmarkedListings((prev) =>
            prev.filter((listingId) => listingId !== id)
          );
        }
      } catch (error) {
        console.error('Error toggling bookmark:', error);
      }
    } else {
      setBookmarkedListings((prevBookmarkedListings) => {
        let newBookmarkedListings;
        if (prevBookmarkedListings.includes(id)) {
          newBookmarkedListings = prevBookmarkedListings.filter(
            (listingId) => listingId !== id
          );
        } else {
          newBookmarkedListings = [...prevBookmarkedListings, id];
        }
        localStorage.setItem(
          'bookmarkedListings',
          JSON.stringify(newBookmarkedListings)
        );
        return newBookmarkedListings;
      });
    }
  };

  return {
    bookmarkedListings,
    handleBookmarkToggle
  };
};
